import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import StoreProductCard, { type StoreProductCardData } from "@/components/store/StoreProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck } from "lucide-react";

type Vendor = {
  id: string;
  display_name: string;
  bio: string | null;
  logo_url: string | null;
  banner_url: string | null;
  identity_verified: boolean;
  rating_avg: number | null;
  rating_count: number;
  total_orders: number;
};

const StoreVendorPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<StoreProductCardData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: v } = await supabase
        .from("store_vendors_public")
        .select(
          "id, display_name, bio, logo_url, banner_url, identity_verified, rating_avg, rating_count, total_orders, slug",
        )
        .eq("slug", slug)
        .maybeSingle();
      if (cancelled) return;
      if (!v) {
        setVendor(null);
        setProducts([]);
        setLoading(false);
        return;
      }
      setVendor(v as Vendor);
      const { data: prods } = await supabase
        .from("store_products")
        .select(
          "id, slug, title, base_price_cents, currency, images, vendor:store_vendors_public!vendor_id(slug, display_name)",
        )
        .eq("status", "active")
        .eq("vendor_id", v.id)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setProducts((prods as unknown as StoreProductCardData[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-7xl px-6 py-8">
        <Link to="/store" className="text-sm text-muted-foreground hover:underline">
          ← Back to Store
        </Link>
        {!vendor && !loading ? (
          <Card className="p-12 text-center mt-6">
            <h1 className="text-xl font-semibold">Vendor not found</h1>
          </Card>
        ) : vendor ? (
          <>
            {vendor.banner_url && (
              <div className="mt-4 h-48 rounded-lg overflow-hidden bg-muted">
                <img src={vendor.banner_url} alt="" className="h-full w-full object-cover" />
              </div>
            )}
            <div className="mt-6 flex items-start gap-4">
              {vendor.logo_url && (
                <img
                  src={vendor.logo_url}
                  alt=""
                  className="h-16 w-16 rounded-full object-cover bg-muted"
                />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-3xl font-bold">{vendor.display_name}</h1>
                  {vendor.identity_verified && (
                    <ShieldCheck className="h-5 w-5 text-primary" aria-label="Verified vendor" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {vendor.total_orders} orders fulfilled
                  {vendor.rating_avg ? ` · ${vendor.rating_avg.toFixed(1)} ★ (${vendor.rating_count})` : ""}
                </p>
                {vendor.bio && (
                  <p className="mt-3 max-w-2xl text-muted-foreground">{vendor.bio}</p>
                )}
              </div>
            </div>

            <div className="mt-10">
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="aspect-square rounded-lg" />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <Card className="p-12 text-center">
                  <p className="text-muted-foreground">No active products yet.</p>
                </Card>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products.map((p) => (
                    <StoreProductCard key={p.id} product={p} />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : null}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreVendorPage;
